import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { RawButton } from '../../../../../CommonComponents/OPL/Button/Button.styles';
import { Dot } from '../../../../../CommonComponents/OPL/Dot/Dot';
import { Text } from '../../../../../CommonComponents/OPL/Text/Text';
import { TruncatedText } from '../../../../../CommonComponents/OPL/Text/TruncatedText';
import { ChartType } from '../../../../../CommonSelectors/CommonArea.types';
import { formatCurrency, formatPercentage } from '../../../../../CommonUtils/formatNumber';

import {
  Sorting,
  SortingDirection,
} from '../../../../tabs/Positions/components/AlternativeDetail/AlternativeDetail.meta';

import { PercentageChart } from '../../../PercentageChart/PercentageChart';
import { StyledHtmlTable } from '../../AssetAllocationChart.styles';
import { PositionsLink } from '../../PositionsLink/PositionsLink';
import { Icon } from '../../../../../CommonComponents/OPL/Icon/Icon';
import { EmptyState } from '../../../../../CommonComponents/OPL/AsyncStates/EmptyState/EmptyState';
import { compareValueStrings } from '../../../../helpers/sortTableRows';
import { useIsEmbeddedMobile } from '../../../../../_hooks/useIsEmbeddedMobile';
import { selectOrionWidgetsChartType } from '../../../../../CommonSelectors/CommonAreaSelector';
import { Cell } from '../../../../../CommonComponents/OPL/HtmlTable/HtmlTable';

interface Props {
  name: string;
  showLink?: boolean;
  chartType?: ChartType;
  chartData: {
    name: string;
    allocation: number;
    marketValue: number;
    color: string;
  }[];
}

export const AssetAllocationChartContent = ({
  name,
  chartData,
  showLink = true,
  chartType,
}: Props) => {
  const [sorting, setSorting] = useState<Sorting>({});

  const isEmbeddedMobile = useIsEmbeddedMobile();

  const selectedChartType = chartType ?? useSelector(selectOrionWidgetsChartType);

  const rows = useMemo(() => {
    if (!chartData || chartData.length === 0) {
      return null;
    }

    if (Object.keys(sorting).length === 0) {
      return chartData;
    }

    const sortingKey = Object.keys(sorting)[0];

    return [...chartData].sort((a, b) =>
      compareValueStrings(a[sortingKey].toString(), b[sortingKey].toString(), sorting[sortingKey]),
    );
  }, [chartData, sorting]);

  return (
    <>
      <Text variant="cardTitle" m="16" mb="0">
        {`${name} Allocation`}
      </Text>

      <Box flex="1">
        <Box px="12" py="24">
          <PercentageChart
            data={chartData.map(point => ({
              ...point,
              label: point.name,
              value: point.allocation,
            }))}
            chartType={isEmbeddedMobile ? ChartType.Bar : selectedChartType}
            size={196}
          />
        </Box>

        <StyledHtmlTable variant="embedded" isEmbeddedMobile={isEmbeddedMobile}>
          <table>
            <colgroup>
              <col width="100%" />
              <col width="0%" />
            </colgroup>

            <thead>
              <tr>
                <th>
                  <RawButton
                    onClick={() => {
                      if (sorting.name) {
                        setSorting(
                          sorting.name === SortingDirection.Asc
                            ? { name: SortingDirection.Desc }
                            : {},
                        );
                      } else {
                        setSorting({ name: SortingDirection.Asc });
                      }
                    }}
                    aria-label="Sort by column"
                    css="width: 100%"
                  >
                    <Box display="flex" alignItems="center" css="gap: 4px" p="2">
                      <Text variant="tableHeader" textAlign="left">
                        {name}
                      </Text>
                      {(sorting.name === SortingDirection.Asc ||
                        sorting.name === SortingDirection.Desc) && (
                        <Icon
                          className={
                            sorting.name === SortingDirection.Asc
                              ? 'fas fa-arrow-up'
                              : 'fas fa-arrow-down'
                          }
                          color="neutral.500"
                        />
                      )}
                    </Box>
                  </RawButton>
                </th>

                <th>
                  <RawButton
                    onClick={() => {
                      if (sorting.marketValue) {
                        setSorting(
                          sorting.marketValue === SortingDirection.Asc
                            ? { marketValue: SortingDirection.Desc }
                            : {},
                        );
                      } else {
                        setSorting({ marketValue: SortingDirection.Asc });
                      }
                    }}
                    aria-label="Sort by column"
                    css="width: 100%"
                  >
                    <Box display="flex" alignItems="center" css="gap: 4px" p="2">
                      <Text variant="tableHeader" textAlign="right">
                        Market Value
                      </Text>
                      {(sorting.marketValue === SortingDirection.Asc ||
                        sorting.marketValue === SortingDirection.Desc) && (
                        <Icon
                          className={
                            sorting.marketValue === SortingDirection.Asc
                              ? 'fas fa-arrow-up'
                              : 'fas fa-arrow-down'
                          }
                          color="neutral.500"
                        />
                      )}
                    </Box>
                  </RawButton>
                </th>

                <th>
                  <RawButton
                    onClick={() => {
                      if (sorting.allocation) {
                        setSorting(
                          sorting.allocation === SortingDirection.Asc
                            ? { allocation: SortingDirection.Desc }
                            : {},
                        );
                      } else {
                        setSorting({ allocation: SortingDirection.Asc });
                      }
                    }}
                    aria-label="Sort by column"
                    css="width: 100%"
                  >
                    <Box display="flex" alignItems="center" css="gap: 4px" p="2">
                      <Text variant="tableHeader" textAlign="right">
                        Allocation
                      </Text>
                      {(sorting.allocation === SortingDirection.Asc ||
                        sorting.allocation === SortingDirection.Desc) && (
                        <Icon
                          className={
                            sorting.allocation === SortingDirection.Asc
                              ? 'fas fa-arrow-up'
                              : 'fas fa-arrow-down'
                          }
                          color="neutral.500"
                        />
                      )}
                    </Box>
                  </RawButton>
                </th>
              </tr>
            </thead>

            <tbody>
              {chartData.length === 0 ? (
                <tr style={{ borderBottom: 'none' }}>
                  <td colSpan={3}>
                    <Box
                      height="100%"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      py="32"
                    >
                      <EmptyState />
                    </Box>
                  </td>
                </tr>
              ) : (
                (rows ?? []).map(({ name: seriesName, marketValue, allocation, color }) => (
                  <tr key={`${seriesName}${color}`}>
                    <Cell isFlex>
                      <Box display="flex" alignItems="center">
                        <Dot bg={color} mr="8" />
                        <TruncatedText fontWeight={500} color="neutral.700" width="100%">
                          {seriesName}
                        </TruncatedText>
                      </Box>
                    </Cell>

                    <Cell>
                      <Text
                        variant="tabular"
                        fontWeight={500}
                        color="neutral.700"
                        textAlign="right"
                      >
                        {formatCurrency(marketValue, { decimalPlaces: 2 })}
                      </Text>
                    </Cell>

                    <Cell>
                      <Text
                        variant="tabular"
                        fontWeight={500}
                        color="neutral.700"
                        textAlign="right"
                      >
                        {formatPercentage(allocation, { decimalPlaces: 2 })}
                      </Text>
                    </Cell>

                    <Cell>
                      <Text
                        variant="tabular"
                        fontWeight={500}
                        color="neutral.600"
                        textAlign="right"
                      >
                        {formatPercentage(allocation, { decimalPlaces: 2 })}
                      </Text>
                      <Text
                        variant="tabular"
                        color="neutral.500"
                        textAlign="right"
                        fontSize="small"
                      >
                        {formatCurrency(marketValue, { decimalPlaces: 2 })}
                      </Text>
                    </Cell>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </StyledHtmlTable>
      </Box>

      {showLink && chartData.length > 0 && <PositionsLink mt="16" name={name} />}
    </>
  );
};
